import { Capacitor } from "@capacitor/core";

/** Onko sovellus natiivikuoressa (Android/iOS) vai selaimessa. */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function nativePlatform(): string {
  return Capacitor.getPlatform(); // "android" | "ios" | "web"
}

/**
 * Rekisteroi laitteen FCM-tokenin natiivialustalla.
 *
 * Kayttaa natiivia Firebase SDK:ta (@capacitor-firebase/messaging), ei
 * kayttojarjestelman omaa push-rajapintaa. Ero on olennainen iOS:lla:
 * @capacitor/push-notifications palauttaa siella APNs-laitetokenin
 * (64 merkkia), johon backend ei voi lahettaa mitaan, koska se kayttaa
 * FCM:aa. Tama plugin palauttaa aidon FCM-tokenin molemmilla alustoilla.
 *
 * Palauttaa null jos kayttaja kieltaa luvan tai token ei ole saatavilla.
 */
export async function getNativePushToken(): Promise<string | null> {
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

  let status = await FirebaseMessaging.checkPermissions();
  if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
    status = await FirebaseMessaging.requestPermissions();
  }
  if (status.receive !== "granted") return null;

  try {
    const { token } = await FirebaseMessaging.getToken();
    return token || null;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error("FCM-tokenin haku epaonnistui:", msg);
    return null;
  }
}

/**
 * Kytkee ilmoitusten napsautukset sovelluksen navigaatioon.
 * Kutsutaan kerran kaynnistyksessa (index.tsx).
 */
export async function initNativePushHandlers(): Promise<void> {
  if (!isNative()) return;
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

  await FirebaseMessaging.addListener("notificationActionPerformed", (action) => {
    const data = action.notification?.data as Record<string, unknown> | undefined;
    const target = data?.url;
    if (typeof target === "string" && target.startsWith("/")) {
      window.location.assign(target);
    }
  });
}

/**
 * Selaimen ilmoituslupa turvallisesti luettuna.
 *
 * WKWebView eika Android WebView toteuta selaimen Notification-rajapintaa
 * lainkaan, joten pelkka `Notification.permission` heittaa natiivikuoressa
 * ReferenceErrorin ja kaataa koko nakyman. Palauttaa "unsupported" silloin
 * kun rajapintaa ei ole.
 */
export function webNotificationPermission(): NotificationPermission | "unsupported" {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

/**
 * Onko ilmoituslupa myonnetty. Natiivissa tila kysytaan
 * FirebaseMessagingilta, selaimessa Notification-rajapinnalta.
 */
export async function notificationsGranted(): Promise<boolean> {
  if (isNative()) {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const status = await FirebaseMessaging.checkPermissions();
    return status.receive === "granted";
  }
  return webNotificationPermission() === "granted";
}
